import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { validateManualCandidateForm } from '../../../lib/utils/csvParser';

const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  linkedin_url: '',
  email: '',
  current_company: '',
  current_position: '',
  location: '',
  phone: '+',
};

const formatPhoneInput = (value) => {
  if (!value) return '+';
  const next = value.startsWith('+') ? value : `+${value.replace(/\+/g, '')}`;
  return `+${next.slice(1).replace(/\D/g, '')}`;
};

const translateFieldErrors = (fieldErrors, t) => {
  const next = {};
  Object.entries(fieldErrors || {}).forEach(([field, message]) => {
    if (!message) return;
    if (message === 'First name is required') {
      next[field] = t('candidates.addFirstNameRequired');
    } else if (message === 'LinkedIn URL is required') {
      next[field] = t('candidates.addLinkedinRequired');
    } else if (message.startsWith('Could not recognize "')) {
      const match = message.match(/^Could not recognize "(.+)" as/);
      next[field] = t('candidates.addLinkedinInvalid', { value: match?.[1] || '' });
    } else if (message === 'Email is required') {
      next[field] = t('candidates.addEmailRequired');
    } else if (message.startsWith('Please enter a valid email address')) {
      next[field] = t('candidates.addEmailInvalid');
    } else if (message.startsWith('Please enter a valid phone number')) {
      next[field] = t('candidates.addPhoneInvalid');
    } else {
      next[field] = message;
    }
  });
  return next;
};

const AddCandidateModal = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY_FORM);
      setError(null);
      setFieldErrors({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !saving) onClose();
  };

  const handleChange = (field) => (e) => {
    const value = field === 'phone' ? formatPhoneInput(e.target.value) : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneValue = form.phone.trim() === '+' ? '' : form.phone.trim();
    const { isValid, fieldErrors: nextFieldErrors, data } = validateManualCandidateForm({
      ...form,
      phone: phoneValue,
    });

    if (!isValid) {
      setFieldErrors(translateFieldErrors(nextFieldErrors, t));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(data);
      onClose();
    } catch (err) {
      setError(err.message || t('candidates.addFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-candidate-dialog-title"
    >
      <div
        className="bg-card rounded-lg border border-border shadow-elevation-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto md:overflow-visible md:max-h-none p-4 md:p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 mb-4">
          <h2 id="add-candidate-dialog-title" className="text-base font-semibold text-foreground">
            {t('candidates.addCandidate')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            aria-label={t('common.close')}
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          noValidate
          className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2.5"
        >
          <Input
            label={t('candidates.addFirstName')}
            value={form.first_name}
            onChange={handleChange('first_name')}
            placeholder={t('candidates.addFirstNamePlaceholder')}
            disabled={saving}
            error={fieldErrors.first_name}
            required
            dense
          />
          <Input
            label={t('candidates.addLastName')}
            value={form.last_name}
            onChange={handleChange('last_name')}
            placeholder={t('candidates.addLastNamePlaceholder')}
            disabled={saving}
            error={fieldErrors.last_name}
            dense
          />

          <div className="sm:col-span-2">
            <Input
              label={t('candidates.addLinkedinUrl')}
              value={form.linkedin_url}
              onChange={handleChange('linkedin_url')}
              placeholder={t('candidates.addLinkedinPlaceholder')}
              disabled={saving}
              error={fieldErrors.linkedin_url}
              description={t('candidates.addLinkedinDescription')}
              required
              dense
            />
          </div>

          <div className="sm:col-span-2">
            <Input
              type="text"
              inputMode="email"
              autoComplete="email"
              label={t('candidates.addEmail')}
              value={form.email}
              onChange={handleChange('email')}
              placeholder={t('candidates.addEmailPlaceholder')}
              disabled={saving}
              error={fieldErrors.email}
              required
              dense
            />
          </div>

          <Input
            label={t('candidates.addCompany')}
            value={form.current_company}
            onChange={handleChange('current_company')}
            placeholder={t('candidates.addCompanyPlaceholder')}
            disabled={saving}
            error={fieldErrors.current_company}
            dense
          />
          <Input
            label={t('candidates.addPosition')}
            value={form.current_position}
            onChange={handleChange('current_position')}
            placeholder={t('candidates.addPositionPlaceholder')}
            disabled={saving}
            error={fieldErrors.current_position}
            dense
          />
          <Input
            label={t('candidates.addLocation')}
            value={form.location}
            onChange={handleChange('location')}
            placeholder={t('candidates.addLocationPlaceholder')}
            disabled={saving}
            error={fieldErrors.location}
            dense
          />
          <Input
            type="text"
            inputMode="tel"
            autoComplete="tel"
            label={t('candidates.addPhone')}
            value={form.phone}
            onChange={handleChange('phone')}
            disabled={saving}
            className="font-data"
            error={fieldErrors.phone}
            description={t('candidates.addPhoneDescription')}
            dense
          />

          {error && (
            <p className="sm:col-span-2 text-xs text-destructive">{error}</p>
          )}

          <div className="sm:col-span-2 flex gap-2 justify-end pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" loading={saving}>
              {t('candidates.addCandidate')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCandidateModal;
